import React, { useState, useEffect, useRef, useMemo } from 'react';
import { fillEmailTemplate, EMAIL_PLACEHOLDER_CHIPS } from '../utils/emailTemplateFill';
import { crmSnippetTemplates } from '../services/crmSnippetTemplates';
import { draftEmailWithAi } from '../services/aiFeatures';
import { useAiAction } from '../hooks/useAiAction';
import { AiSparkButton, AiErrorBanner, AiNoKeyHint } from './AiAssist';
import { emailSignatureService } from '../services/emailSignatureService';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL || '';

const DEFAULT_TEMPLATES = [
  {
    id: 1,
    name: 'Erstanruf Nachfassung',
    subject: 'Unser Gespräch von heute – nächste Schritte',
    body: `Hallo {{vorname}},\n\nvielen Dank für das heutige Gespräch! Es war mir eine Freude, mehr über {{firma}} zu erfahren.\n\nWie besprochen würde ich Ihnen gerne zeigen, wie wir Ihnen helfen können, die besten Kandidaten für Ihre offenen Stellen zu gewinnen.\n\nIch schlage vor, dass wir uns in den nächsten Tagen kurz zusammensetzen – 20 Minuten reichen völlig aus.\n\nWann passt es Ihnen am besten?\n\nMit freundlichen Grüßen\nRobin Obrecht\nAdstark® Recruiting`,
  },
  {
    id: 2,
    name: 'Follow-Up nach Kein-Bedarf',
    subject: 'Kurze Rückmeldung – {{firma}}',
    body: `Hallo {{vorname}},\n\nich melde mich noch einmal kurz, da sich die Situation bei vielen unserer Kunden nach einigen Wochen geändert hat.\n\nFalls bei {{firma}} wieder Bedarf an qualifiziertem Personal besteht, stehe ich gerne zur Verfügung.\n\nBeste Grüße\nRobin Obrecht`,
  },
  {
    id: 3,
    name: 'Terminbestätigung',
    subject: 'Bestätigung: Unser Termin am {{datum}}',
    body: `Hallo {{vorname}},\n\nich bestätige hiermit unseren Termin am {{datum}} um {{uhrzeit}} Uhr.\n\nWir werden besprechen:\n- Ihre aktuellen Recruiting-Herausforderungen\n- Wie Adstark® Sie dabei unterstützen kann\n- Konkrete nächste Schritte\n\nBei Fragen stehe ich jederzeit zur Verfügung.\n\nMit freundlichen Grüßen\nRobin Obrecht\nAdstark® Recruiting`,
  },
  {
    id: 4,
    name: 'Angebot / Unterlagen (kurz)',
    subject: 'Unterlagen – {company}',
    body: `Hallo {name},

wie besprochen sende ich Ihnen die Unterlagen zu unserem Angebot für {company}.

Bei Rückfragen melden Sie sich gern.

Viele Grüße`,
  },
];

function loadTemplates() {
  try { return JSON.parse(localStorage.getItem('crm-templates')) || DEFAULT_TEMPLATES; }
  catch { return DEFAULT_TEMPLATES; }
}

function getContacts(lead) {
  const domain = lead.email?.split('@')[1] || (lead.company?.toLowerCase().replace(/[^a-z0-9]/g, '') + '.de');
  const contacts = [
    { id: 1, name: lead.name, role: 'Entscheider', email: lead.email, primary: true },
  ];
  if (lead.jobs > 0) {
    contacts.push({ id: 2, name: `HR – ${lead.company}`, role: 'Personalwesen', email: `hr@${domain}` });
  }
  contacts.push({ id: 3, name: `Info – ${lead.company}`, role: 'Allgemein', email: `info@${domain}` });
  return contacts;
}

export default function FloatingEmail({ lead, onClose }) {
  const templates = loadTemplates();
  const [selId, setSelId]   = useState(templates[0]?.id ?? null);
  const [snippetOverride, setSnippetOverride] = useState(null);
  const [copied, setCopied] = useState(null);
  const [pos, setPos]       = useState({ x: Math.max(40, window.innerWidth / 2 - 360), y: Math.max(40, window.innerHeight / 2 - 290) });
  const [minimized, setMinimized] = useState(false);
  const [emailContext, setEmailContext] = useState('');
  const ai = useAiAction();
  const trackId = useRef((() => { try { return crypto.randomUUID(); } catch { return Math.random().toString(36).slice(2); } })());

  const emailSnippets = useMemo(() => {
    const pack = crmSnippetTemplates.get();
    const branch = (lead?.branch || '').toLowerCase();
    return (pack.emailByBranch || [])
      .filter(e => !e.branch || branch.includes((e.branch || '').toLowerCase()))
      .slice(0, 8);
  }, [lead?.branch]);

  useEffect(() => {
    setSnippetOverride(null);
  }, [selId, lead?.id]);

  const dragging = useRef(false);
  const dragOff  = useRef({ x: 0, y: 0 });

  useEffect(() => {
    function onMove(e) { if (!dragging.current) return; setPos({ x: e.clientX - dragOff.current.x, y: e.clientY - dragOff.current.y }); }
    function onUp()   { dragging.current = false; }
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
  }, []);

  function startDrag(e) {
    if (e.target.closest('button, input, select, textarea')) return;
    dragging.current = true;
    dragOff.current = { x: e.clientX - pos.x, y: e.clientY - pos.y };
    e.preventDefault();
  }

  function copy(text, key) {
    navigator.clipboard.writeText(text).catch(() => {});
    setCopied(key);
    setTimeout(() => setCopied(null), 2000);
  }

  const tpl     = templates.find(t => t.id === selId);
  const hasPreview = Boolean(tpl || snippetOverride);
  const subject = fillEmailTemplate((snippetOverride?.subject ?? tpl?.subject) || '', lead);
  const rawBody = fillEmailTemplate((snippetOverride?.body ?? tpl?.body) || '', lead);
  const signature = emailSignatureService.get(null);
  const body = signature ? `${rawBody}\n\n--\n${signature}` : rawBody;
  const contacts = getContacts(lead);

  // Append tracking pixel if Supabase is configured
  const trackingPixel = SUPABASE_URL && lead?.id
    ? `\n\n<img src="${SUPABASE_URL}/functions/v1/track-open?id=${trackId.current}&lead=${lead.id}" width="1" height="1" style="display:none" />`
    : '';
  const bodyWithTracking = body + trackingPixel;
  const mailtoHref = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(bodyWithTracking)}`;

  return (
    <div style={{
      position: 'fixed', left: pos.x, top: pos.y, zIndex: 400,
      width: 720,
      background: 'var(--glass-modal)',
      backdropFilter: 'var(--glass-blur-lg)',
      WebkitBackdropFilter: 'var(--glass-blur-lg)',
      borderRadius: 16, border: '1px solid rgba(255,255,255,0.80)',
      boxShadow: '0 32px 80px rgba(15,40,100,0.22), 0 8px 24px rgba(15,40,100,0.12), 0 0 0 0.5px rgba(255,255,255,0.9), inset 0 1px 0 rgba(255,255,255,1)',
      overflow: 'hidden', fontFamily: 'var(--font)',
      display: 'flex', flexDirection: 'column',
    }}>

      {/* ── Header ── */}
      <div onMouseDown={startDrag} style={{
        background: 'linear-gradient(135deg, rgba(14,110,150,0.88), rgba(49,134,186,0.75))',
        backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
        padding: '11px 14px',
        cursor: 'grab', userSelect: 'none',
        display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0,
        borderBottom: '1px solid rgba(255,255,255,0.18)',
      }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.9)" strokeWidth="1.8">
          <rect x="2" y="4" width="20" height="16" rx="2"/><path d="M2 7l10 7 10-7"/>
        </svg>
        <span style={{ fontSize: 13, fontWeight: 600, color: '#fff', flex: 1, letterSpacing: '-0.01em' }}>E-Mail – {lead.name}</span>
        <div style={{ display: 'flex', gap: 4 }}>
          <HdrBtn onClick={() => setMinimized(m => !m)} title="Minimieren">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.75)" strokeWidth="2"><line x1="5" y1="12" x2="19" y2="12"/></svg>
          </HdrBtn>
          <HdrBtn onClick={onClose} title="Schließen">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.75)" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </HdrBtn>
        </div>
      </div>

      {!minimized && (
        <div style={{ display: 'flex', height: 480, overflow: 'hidden' }}>

          {/* ── Left: template list ── */}
          <div style={{ width: 200, minWidth: 200, borderRight: '1px solid var(--border)', background: 'var(--bg3)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div style={{ padding: '10px 12px 8px', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Vorlagen</span>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: '6px 8px' }}>
              {templates.map(t => {
                const active = selId === t.id;
                return (
                  <button key={t.id} onClick={() => { setSelId(t.id); setSnippetOverride(null); }} style={{
                    width: '100%', textAlign: 'left', padding: '8px 10px', borderRadius: 8, marginBottom: 2,
                    background: active ? 'var(--accent-bg)' : 'transparent',
                    border: active ? '1px solid var(--accent-border)' : '1px solid transparent',
                    color: active ? 'var(--accent)' : 'var(--text)',
                    fontSize: 12, fontWeight: active ? 600 : 400, cursor: 'pointer', transition: 'all .1s',
                  }}
                  onMouseEnter={e => { if (!active) e.currentTarget.style.background = 'var(--bg4)'; }}
                  onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent'; }}
                  >
                    <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.name}</div>
                    {t.subject && <div style={{ fontSize: 10, color: active ? 'var(--accent)' : 'var(--text3)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', opacity: 0.8 }}>{t.subject}</div>}
                  </button>
                );
              })}
            </div>

                {/* CRM-Snippets (Einstellungen / Vorlagen) */}
                {emailSnippets.length > 0 && (
                  <div style={{ padding: '8px 12px 6px', borderTop: '1px solid var(--border)', flexShrink: 0 }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.07em' }}>CRM-Snippets</span>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6 }}>
                      {emailSnippets.map(s => (
                        <button key={s.id} type="button" onClick={() => setSnippetOverride({ id: s.id, subject: s.subject || '', body: s.body || '' })}
                          style={{ fontSize: 10, padding: '4px 8px', borderRadius: 6, border: '1px solid var(--border)', background: snippetOverride?.id === s.id ? 'var(--accent-bg)' : 'var(--bg2)', cursor: 'pointer', color: 'var(--text)', textAlign: 'left' }}>
                          {s.branch || 'Snippet'}
                        </button>
                      ))}
                    </div>
                    {snippetOverride && (
                      <button type="button" onClick={() => setSnippetOverride(null)} style={{ marginTop: 6, fontSize: 10, border: 'none', background: 'none', color: 'var(--accent)', cursor: 'pointer', padding: 0 }}>
                        Zurück zur gewählten Vorlage
                      </button>
                    )}
                  </div>
                )}

            {/* ── Ansprechpartner ── */}
            <div style={{ borderTop: '1px solid var(--border)', flexShrink: 0 }}>
              <div style={{ padding: '10px 12px 6px' }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Ansprechpartner</span>
              </div>
              <div style={{ padding: '0 8px 10px', display: 'flex', flexDirection: 'column', gap: 4 }}>
                {contacts.map(c => (
                  <div key={c.id} style={{ background: 'var(--bg2)', borderRadius: 8, border: '1px solid var(--border)', padding: '8px 10px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 4, marginBottom: 4 }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</div>
                        <div style={{ fontSize: 10, color: 'var(--text3)' }}>{c.role}</div>
                      </div>
                      {c.primary && <span style={{ fontSize: 9, fontWeight: 700, background: 'var(--accent)', color: '#fff', borderRadius: 4, padding: '1px 5px', flexShrink: 0 }}>Primär</span>}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <span style={{ fontSize: 11, color: 'var(--accent)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{c.email}</span>
                      <CopyBtn small text={c.email} id={`email-${c.id}`} copied={copied} onCopy={copy} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* ── Right: template preview ── */}
          <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: 'var(--bg)' }}>
            {hasPreview ? (
              <div style={{ flex: 1, overflowY: 'auto', padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>

                <div style={{ background: 'var(--bg2)', borderRadius: 10, border: '1px solid var(--border)', padding: '12px 14px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.07em' }}>KI-Entwurf</span>
                    <AiSparkButton small label="Generieren" loading={ai.loading} disabled={!emailContext.trim()}
                      onClick={async () => {
                        const result = await ai.run(() => draftEmailWithAi({
                          leadName: lead?.name, company: lead?.company, email: lead?.email, branch: lead?.branch,
                          context: emailContext.trim(),
                        }));
                        if (result?.subject || result?.body) {
                          setSnippetOverride({ id: 'ai-draft', subject: result.subject || '', body: result.body || '' });
                        }
                      }}
                    />
                  </div>
                  {!ai.configured && <AiNoKeyHint compact />}
                  <textarea value={emailContext} onChange={e => setEmailContext(e.target.value)}
                    placeholder="Stichworte für die KI…" rows={2}
                    style={{ width: '100%', boxSizing: 'border-box', borderRadius: 8, border: '1.5px solid var(--border)', padding: '8px 10px', fontSize: 12, fontFamily: 'inherit', resize: 'vertical', background: 'var(--bg)' }}
                  />
                  <AiErrorBanner message={ai.error} onDismiss={ai.clearError} />
                </div>

                {/* Subject */}
                <div style={{ background: 'var(--bg2)', borderRadius: 10, border: '1px solid var(--border)', padding: '12px 14px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Betreff</span>
                    <CopyBtn text={subject} id="subj" copied={copied} onCopy={copy} />
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--text)' }}>{subject || <span style={{ color: 'var(--text3)' }}>Kein Betreff</span>}</div>
                </div>

                {/* Body */}
                <div style={{ background: 'var(--bg2)', borderRadius: 10, border: '1px solid var(--border)', padding: '12px 14px', flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Nachricht</span>
                    <CopyBtn text={body} id="body" copied={copied} onCopy={copy} />
                  </div>
                  <pre style={{ fontSize: 13, color: 'var(--text)', whiteSpace: 'pre-wrap', fontFamily: 'var(--font)', lineHeight: 1.75, margin: 0 }}>{body}</pre>
                </div>

                {/* Placeholders hint */}
                <div style={{ background: 'var(--accent-bg)', borderRadius: 8, border: '1px solid var(--accent-border)', padding: '9px 12px' }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--accent)', marginBottom: 5 }}>Platzhalter (klicken zum Kopieren)</div>
                  <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                    {EMAIL_PLACEHOLDER_CHIPS.map(p => (
                      <code key={p} onClick={() => copy(p, p)} style={{
                        fontSize: 10, background: 'var(--bg2)', border: '1px solid var(--accent-border)',
                        borderRadius: 4, padding: '2px 6px', color: 'var(--accent)', cursor: 'pointer',
                        fontFamily: 'var(--mono)',
                      }}>{copied === p ? '✓' : p}</code>
                    ))}
                  </div>
                </div>

                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                  <a
                    href={mailtoHref}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6,
                      height: 34, padding: '0 14px', borderRadius: 8,
                      background: 'linear-gradient(135deg,#0f8a53,#159c5f)', color: '#fff',
                      fontSize: 12, fontWeight: 600, textDecoration: 'none',
                      boxShadow: '0 2px 8px rgba(29,190,116,0.35)',
                    }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                    In Mail-App öffnen
                  </a>
                  <span style={{ fontSize: 11, color: 'var(--text3)' }}>Betreff und Text werden übernommen · Vorlagen bearbeiten unter „Vorlagen“</span>
                </div>
              </div>
            ) : (
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text3)', fontSize: 14, padding: 24, textAlign: 'center' }}>
                Vorlage links wählen oder KI-Entwurf mit Stichworten generieren
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ── helpers ── */
function HdrBtn({ onClick, title, children }) {
  return (
    <button onClick={onClick} title={title} style={{ width: 24, height: 24, borderRadius: 5, background: 'rgba(255,255,255,0.08)', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, transition: 'background .1s' }}
      onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.18)'}
      onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.08)'}
    >{children}</button>
  );
}

function CopyBtn({ text, id, copied, onCopy, small }) {
  const done = copied === id;
  return (
    <button onClick={() => onCopy(text, id)} style={{
      height: small ? 22 : 26, padding: small ? '0 7px' : '0 10px',
      borderRadius: 5, fontSize: small ? 10 : 11, fontWeight: 500,
      background: done ? 'rgba(22,163,74,0.12)' : 'var(--bg4)',
      color: done ? '#16a34a' : 'var(--text2)',
      border: done ? '1px solid rgba(22,163,74,0.3)' : '1px solid var(--border)',
      cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 3,
      transition: 'all .15s', flexShrink: 0, whiteSpace: 'nowrap',
    }}>
      {done ? (
        <><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M20 6L9 17l-5-5"/></svg>{small ? '' : ' Kopiert!'}</>
      ) : (
        <><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>{small ? '' : ' Kopieren'}</>
      )}
    </button>
  );
}
